import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * New Audit dialog — hits the legacy /api/training-style generic /audits
 * endpoint so CSV import + PDF export contract stays intact. Adds the new
 * `category`, `cqc_key_questions`, `cqc_quality_statements` fields.
 */
export default function AuditNewDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(defaults());
  const [templates, setTemplates] = useState([]);
  const [constants, setConstants] = useState({ categories: [], cqc_key_questions: [], cqc_quality_statements: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(defaults());
    api.get("/audits-qa/templates", { params: { active_only: true } }).then((r) => setTemplates(r.data.items || [])).catch(() => {});
    api.get("/audits-qa/constants").then((r) => setConstants(r.data)).catch(() => {});
  }, [open]);

  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const pickTemplate = (id) => {
    patch("template_id", id || null);
    const t = templates.find((x) => x.id === id);
    if (t) {
      patch("title", t.name);
      patch("category", t.category);
      patch("audit_type", t.category);   // legacy field mirror
      patch("cqc_key_questions", t.cqc_key_questions || []);
      patch("kloes", t.cqc_key_questions || []);
    }
  };
  const toggleFive = (k) => patch("cqc_key_questions", form.cqc_key_questions?.includes(k) ? form.cqc_key_questions.filter((x) => x !== k) : [...(form.cqc_key_questions || []), k]);
  const toggleQS = (label) => patch("cqc_quality_statements", form.cqc_quality_statements?.includes(label) ? form.cqc_quality_statements.filter((x) => x !== label) : [...(form.cqc_quality_statements || []), label]);

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title required");
    if (!form.conducted_date) return toast.error("Date required");
    setSaving(true);
    try {
      // POST to the generic /api/audits so the record lives in db.audits
      const body = { ...form, kloes: form.cqc_key_questions || [] };
      const r = await api.post("/audits", { data: body });
      toast.success("Audit created");
      onSaved?.(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="audit-new-dialog">
        <DialogHeader><DialogTitle>New Audit</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <FL label="Start from template (optional)">
              <select value={form.template_id || ""} onChange={(e) => pickTemplate(e.target.value)} className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2" data-testid="audit-new-template">
                <option value="">— Blank —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </FL>
          </div>
          <div className="col-span-2"><FL label="Title *"><Input value={form.title} onChange={(e) => patch("title", e.target.value)} data-testid="audit-new-title" /></FL></div>
          <FL label="Category">
            <select value={form.category} onChange={(e) => { patch("category", e.target.value); patch("audit_type", e.target.value); }} className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2" data-testid="audit-new-category">
              {(constants.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </FL>
          <FL label="Date *"><Input type="date" value={form.conducted_date} onChange={(e) => patch("conducted_date", e.target.value)} data-testid="audit-new-date" /></FL>
          <FL label="Auditor"><Input value={form.auditor} onChange={(e) => patch("auditor", e.target.value)} data-testid="audit-new-auditor" /></FL>
          <FL label="Score (%)"><Input type="number" min="0" max="100" value={form.score} onChange={(e) => patch("score", e.target.value)} data-testid="audit-new-score" /></FL>
          <div className="col-span-2">
            <FL label="CQC Five Key Questions">
              <div className="flex flex-wrap gap-2 mt-1">
                {(constants.cqc_key_questions || []).map((k) => (
                  <label key={k} className="flex items-center gap-1 text-xs text-slate-700">
                    <input type="checkbox" checked={form.cqc_key_questions?.includes(k)} onChange={() => toggleFive(k)} data-testid={`audit-new-kq-${k}`} /> {k}
                  </label>
                ))}
              </div>
            </FL>
          </div>
          <div className="col-span-2">
            <FL label="Quality Statements (optional)">
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2 bg-white">
                {(constants.cqc_quality_statements || []).filter((q) => !form.cqc_key_questions?.length || form.cqc_key_questions.includes(q.key_question)).map((q, i) => (
                  <label key={i} className="flex items-center gap-1 text-xs text-slate-700 py-0.5">
                    <input type="checkbox" checked={form.cqc_quality_statements?.includes(q.label)} onChange={() => toggleQS(q.label)} data-testid={`audit-new-qs-${i}`} />
                    <span className="text-slate-500">{q.key_question} —</span> {q.label}
                  </label>
                ))}
              </div>
            </FL>
          </div>
          <div className="col-span-2"><FL label="Findings summary"><Textarea rows={3} value={form.findings} onChange={(e) => patch("findings", e.target.value)} data-testid="audit-new-findings" /></FL></div>
          <div className="col-span-2"><FL label="Action plan"><Textarea rows={2} value={form.action_plan} onChange={(e) => patch("action_plan", e.target.value)} data-testid="audit-new-action" /></FL></div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-audit-new-save">{saving ? "Saving…" : "Create Audit"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function defaults() {
  return {
    template_id: null, title: "", category: "other", audit_type: "other",
    conducted_date: new Date().toISOString().slice(0, 10),
    auditor: "", score: "",
    cqc_key_questions: [], cqc_quality_statements: [],
    kloes: [], status: "in_progress", findings: "", action_plan: "",
  };
}

function FL({ label, children }) { return <label className="block"><span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span><div className="mt-0.5">{children}</div></label>; }
